export interface AnalysisJob {
  id: string;
  user_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'needs_continue';
  stage:
    | 'queued'
    | 'extracting_text'
    | 'parsing_structure'
    | 'validating_structure'
    | 'reconciling_structure'
    | 'breaking_down_scenes'
    | 'analyzing_narrative'
    | 'generating_budget'
    | 'generating_schedule'
    | 'generating_financing'
    | 'generating_distribution'
    | 'finalizing';
  progress: number;
  file_name: string;
  file_type: 'pdf' | 'fdx' | 'fountain';
  total_scenes: number | null;
  scenes_processed: number;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  formato?: 'largo' | 'corto' | 'serie' | string | null;
  tipo?: 'ficcion' | 'documental' | string | null;
  num_capitulos?: number | null;
  /** Timestamp ISO cuando el agente Localizador consolidó los slug-lines crudos
   *  en localizaciones reales. NULL = aún no consolidado (job antiguo o en curso). */
  locations_consolidated_at?: string | null;

  // ── V3 · versiones de guion ─────────────────────────────────────────────
  /** Identidad estable del proyecto: todas las versiones comparten project_id.
   *  Jobs antiguos: la migración lo backfilleó = su propio id. */
  project_id?: string | null;
  /** Nº de versión dentro del proyecto (1 = primer análisis). */
  version?: number | null;
  /** Job de la versión anterior (null en la v1 / análisis normal). */
  parent_job_id?: string | null;
  /** 'incremental' = reutilizó lo no cambiado; 'full'/null = análisis completo. */
  reanalysis_mode?: 'full' | 'incremental' | null;
  /** Resumen del diff contra la versión anterior (solo en incrementales). */
  diff_summary?: DiffSummary | null;
}

/** Resumen del diff entre dos versiones (analyze-script/pipeline/version-diff.ts). */
export interface DiffSummary {
  igual: number;
  editada: number;
  nueva: number;
  borrada: number;
  total_nuevo: number;
  reused_pct: number;
}

export interface Scene {
  id: string;
  job_id: string;
  scene_number: number;
  header: string;
  int_ext: 'INT' | 'EXT' | 'INT/EXT';
  location_name: string;
  /** FK opcional a la fuente canónica `locations.id`. Si está rellenado,
   *  schedule/budget/dashboard cuentan por aquí (no por el string libre). */
  location_id?: string | null;
  time_of_day: string;
  page_start: number | null;
  page_end: number | null;
  page_eighths: number;
  raw_text: string | null;
  synopsis: string | null;
  confidence: number;
  status: 'draft' | 'confirmed' | 'rejected';
  breakdown?: SceneBreakdown;
  has_vehicle_travel: boolean;
  vehicle_travel_origin: string | null;
  vehicle_travel_destination: string | null;
  dia_ficcion?: string | null;
  created_at: string;
}

export interface Character {
  id: string;
  job_id: string;
  name: string;
  type: 'speaking' | 'extra' | 'mentioned';
  confidence: number;
  status: 'draft' | 'confirmed' | 'rejected';
  scene_ids: string[];
  created_at: string;
}

export type LocationType =
  | 'private'
  | 'public_municipal'
  | 'public_autonomic'
  | 'public_state'
  | 'BIC'
  | 'patrimonio_nacional'
  | 'natural_protegido'
  | 'studio'
  | 'otros';

export type PermitStatus = 'none' | 'needed' | 'requested' | 'granted' | 'denied';
export type LocationSource = 'analyzer' | 'user' | 'agent' | 'imported';
export type TruckSize = 'furgo' | 'camion' | 'plataforma' | 'sin_acceso';

export interface Location {
  id: string;
  job_id: string;
  name: string;
  parent_id: string | null;
  confidence: number;
  status: 'draft' | 'confirmed' | 'rejected';
  variants?: Location[];
  scene_count?: number;

  // Geo
  lat?: number;
  lng?: number;
  address?: string;
  place_id?: string;
  space_type?: 'natural' | 'studio' | 'built_set';

  // Localizador · administrativo
  location_type?: LocationType | null;
  permit_status?: PermitStatus;
  permit_authority?: string | null;
  permit_deadline?: string | null; // YYYY-MM-DD
  permit_notes?: string | null;

  // Localizador · contacto
  contact_name?: string | null;
  contact_role?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;

  // Localizador · económico (alimenta Cap 05 ICAA)
  cost_cession_per_day?: number | null;
  cost_permit_total?: number | null;
  cost_insurance_extra?: number | null;
  cost_base_camp?: number | null;
  cost_recovery?: number | null;
  cost_notes?: string | null;

  // Localizador · logística (alimenta AD)
  availability_start?: string | null;
  availability_end?: string | null;
  daily_hours_start?: string | null; // HH:MM
  daily_hours_end?: string | null;
  max_crew?: number | null;
  max_truck_size?: TruckSize | null;
  distance_to_base_km?: number | null;
  base_camp_notes?: string | null;
  access_notes?: string | null;

  // Localizador · meta
  scripted_aliases?: string[];
  notes?: string | null;
  source?: LocationSource;

  created_at: string;
  updated_at?: string;
}

// Production breakdown per scene (stored as JSONB in scenes.breakdown)
export interface SceneBreakdown {
  scene_number: number;
  cast: { name: string; type: "speaking" | "extra" | "mentioned"; dialogue_lines: number; casting_notes?: string }[];
  locations: { name: string; space_type: "natural" | "studio" | "built_set"; requires_permit: boolean; set_construction?: string }[];
  props: { item: string; type: "action" | "ambient" | "food" | "weapon"; rented_or_purchased: "rent" | "purchase" | "unknown"; quantity: number }[];
  costumes: { character: string; description: string; period?: string; change_from_previous: boolean }[];
  makeup: { character: string; effect_type: string; complexity: "simple" | "medium" | "complex"; estimated_minutes?: number }[];
  vehicles: { type: string; is_action_vehicle: boolean; period?: string; camera_car_needed: boolean }[];
  sfx: { type: string; complexity: "low" | "medium" | "high"; risk_level: "low" | "medium" | "high"; material_needed?: string }[];
  vfx: { type: string; complexity: "simple" | "medium" | "complex"; estimated_shots?: number }[];
  stunts: { type: string; risk_level: "low" | "medium" | "high"; doubles_needed: number; rehearsal_days?: number }[];
  animals: { species: string; quantity: number; actor_interaction: boolean; regulation_notes?: string }[];
  music: { type: string; requires_rights: boolean; requires_set_playback: boolean }[];
  sound?: { conditions: string; special_recording?: string; foley_notes?: string };
  camera?: { required_lighting: string; special_equipment: string[]; special_format?: string };
  continuity?: { connected_scenes: number[]; raccord_elements: string[]; narrative_time: string; safety_notes?: string };
}

export interface AggregatedCast {
  id: string;
  name: string;
  type: "speaking" | "extra" | "mentioned";
  dialogue_lines: number;
  scene_count: number;
  scenes: number[];
  casting_notes?: string;
  confidence: number;
  status: "draft" | "confirmed" | "rejected";
}

// Narrative analysis types
export interface NarrativeAnalysis {
  id: string;
  job_id: string;
  paradigm: "three_act" | "five_act" | "non_conventional";
  genre: string;
  subgenres: string[];
  tone: string;
  references: string[];
  audience_estimate: string;
  turning_points: { scene_number: number; type: string; description: string }[];
  acts: { act1_scenes: number[]; act2_scenes: number[]; act3_scenes: number[] };
}

export interface CharacterArc {
  id: string;
  job_id: string;
  name: string;
  objective: string;
  obstacle: string;
  transformation: string;
  entry_scene: number;
  key_scene: number;
  exit_scene: number;
  relationships: { character: string; type: string }[];
  presence_pct: number;
}

export interface EmotionalMapItem {
  id: string;
  job_id: string;
  scene_number: number;
  emotion: string;
  intensity: number;
  rhythm: "slow" | "medium" | "fast";
  change: "escalation" | "descent" | "rupture" | "stable";
}

export interface NarrativeDiagnostic {
  id: string;
  job_id: string;
  type: "strength" | "problem";
  description: string;
  scenes: number[];
  severity: "high" | "medium" | "low";
  suggestion?: string;
  /** Set by backend for criteria-based scoring; older rows may be null */
  area?: 'estructura' | 'personajes' | 'emociones' | 'ritmo' | 'dialogos' | 'temas';
}

// Re-export schedule types for convenience
export type { SceneScheduleEntry, ShootingDayPlan, StripColor, ScheduleStats, DoodEntry, DoodStatus } from '@/lib/schedule-algorithm';

export interface CastingProfile {
  id: string;
  job_id: string;
  character_name: string;
  age_min: number;
  age_max: number;
  gender: string;
  skills: string[];
  acting_level: "lead_experienced" | "lead_newcomer" | "supporting" | "minor";
  shooting_days: number;
  complexity_flags: string[];
  reference_profile: string;
}

export interface Conflict {
  id: string;
  job_id: string;
  description: string;
  type: 'person_vs_self' | 'person_vs_person' | 'person_vs_society' | 'person_vs_nature';
  characters: string[];
  confrontation_scenes: number[];
  resolution: string;
  related_themes: string[];
  created_at: string;
}

export interface Theme {
  id: string;
  job_id: string;
  name: string;
  type: 'main' | 'secondary';
  description: string;
  motifs: string[];
  embodied_by: string[];
  scene_numbers: number[];
  created_at: string;
}

export type AnalysisStage = AnalysisJob['stage'];
export type AnalysisStatus = AnalysisJob['status'];
export type FileType = AnalysisJob['file_type'];

export interface AnalysisSummary {
  total_scenes: number;
  total_characters: number;
  total_locations: number;
  total_pages: number;
}

export type ConfidenceLevel = 'high' | 'medium' | 'low';

export function getConfidenceLevel(confidence: number): ConfidenceLevel {
  if (confidence >= 0.8) return 'high';
  if (confidence >= 0.5) return 'medium';
  return 'low';
}

export const STAGE_LABELS: Record<AnalysisStage, string> = {
  queued: "En cola",
  extracting_text: "Extrayendo texto...",
  parsing_structure: "Detectando escenas...",
  validating_structure: "Validando estructura...",
  reconciling_structure: "Reconciliando resultados...",
  breaking_down_scenes: "Desglosando escenas...",
  analyzing_narrative: "Analizando narrativa...",
  generating_budget: "Generando presupuesto...",
  generating_schedule: "Generando plan de rodaje...",
  generating_financing: "Mapa de financiación...",
  generating_distribution: "Estrategia de distribución...",
  finalizing: "Finalizando...",
};

export const STAGE_PROGRESS_RANGES: Record<AnalysisStage, [number, number]> = {
  queued: [0, 0],
  extracting_text: [0, 10],
  parsing_structure: [10, 15],
  validating_structure: [15, 40],
  reconciling_structure: [40, 45],
  breaking_down_scenes: [45, 85],
  analyzing_narrative: [85, 92],
  generating_budget: [92, 95],
  generating_schedule: [95, 97],
  generating_financing: [97, 98],
  generating_distribution: [98, 99],
  finalizing: [99, 100],
};

// Stage management
export type StageName = 'narrativa' | 'moodboard' | 'localizaciones' | 'rodaje' | 'casting' | 'diseno_produccion' | 'postproduccion' | 'presupuesto' | 'eco' | 'financiacion' | 'audiencias' | 'festivales';
export type StageStatus = 'locked' | 'available' | 'in_progress' | 'completed';

export interface StageStatusRecord {
  id: string;
  job_id: string;
  stage: StageName;
  status: StageStatus;
  completed_at: string | null;
  created_at: string;
}

export interface ProjectContent {
  id: string;
  job_id: string;
  content_type: 'logline' | 'synopsis_short' | 'synopsis_long' | 'treatment' | 'director_notes' | 'production_notes';
  content: string;
  generation_params: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface ChatMessage {
  id: string;
  job_id: string;
  stage: string;
  role: 'user' | 'assistant';
  content: string;
  user_name: string | null;
  actions: ChatAction[] | null;
  actions_applied: boolean;
  created_at: string;
}

export interface ChatAction {
  table: string;
  operation: 'update' | 'insert' | 'delete';
  id?: string;
  data?: Record<string, unknown>;
}

export const STAGE_DEPENDENCIES: Record<StageName, StageName[]> = {
  narrativa: [],
  moodboard: ['narrativa'],
  localizaciones: ['narrativa'],
  rodaje: ['narrativa', 'localizaciones'],
  casting: ['narrativa'],
  diseno_produccion: ['narrativa'],
  postproduccion: ['rodaje'],
  presupuesto: ['rodaje', 'localizaciones'],
  // La huella se calcula del plan de rodaje (km, jornadas, noches de hotel) y de ciertas
  // cuentas del presupuesto (vía monetaria). Sin uno de los dos no hay nada que estimar.
  eco: ['rodaje', 'presupuesto'],
  financiacion: ['presupuesto'],
  audiencias: ['narrativa'],
  festivales: ['narrativa'],
};

export const STAGE_CONFIG: { name: StageName; label: string; expert: string }[] = [
  { name: 'narrativa', label: 'Narrativa', expert: 'Script Doctor' },
  { name: 'moodboard', label: 'Moodboard', expert: 'Director de Arte' },
  { name: 'localizaciones', label: 'Localizaciones', expert: 'Jefe/a de Localizaciones' },
  { name: 'rodaje', label: 'Plan de Rodaje', expert: 'Ayudante de Dirección' },
  { name: 'casting', label: 'Casting', expert: 'Director de Casting' },
  { name: 'diseno_produccion', label: 'Diseño de Producción', expert: 'Diseñador/a de Producción' },
  { name: 'postproduccion', label: 'Postproducción / VFX', expert: 'Coordinador/a de Post y VFX' },
  { name: 'presupuesto', label: 'Presupuesto', expert: 'Line Producer' },
  { name: 'eco', label: 'Huella de Carbono', expert: 'Eco Manager' },
  { name: 'financiacion', label: 'Financiación', expert: 'Productor Ejecutivo' },
  { name: 'audiencias', label: 'Diseño de Audiencias', expert: 'Agente de Ventas' },
  { name: 'festivales', label: 'Festivales y Distribución', expert: 'Distribuidor/a' },
];

// Etapas que tienen CHAT propio pero que NO se pintan como etapa del pipeline en el
// sidebar, porque su sitio en el menú ya existe como item independiente:
//   festivales     -> "Plan Festivales" (cuelga de Audiencias)
//   postproduccion -> "Breakdown VFX" (sección Proyecto)
//   eco            -> "Huella de Carbono" (justo detrás de Presupuesto)
// Si se recorren con el resto de STAGE_CONFIG, esas pestañas salen DOS VECES en el menú
// (y dos veces en la lista de pestañas limitables por el owner). La lista vive aquí, y no
// duplicada en cada fichero, para que al dar de alta un agente nuevo no se quede una de
// las dos exclusiones sin actualizar.
export const STAGES_FUERA_DEL_PIPELINE: readonly StageName[] = ['festivales', 'postproduccion', 'eco'];

/** true si la etapa se pinta como etapa del pipeline en la navegación. */
export function esStageDePipeline(name: StageName): boolean {
  return !STAGES_FUERA_DEL_PIPELINE.includes(name);
}
